import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Webhook,
} from 'lucide-react';
import { useWebhookLogs } from '../../hooks/useWebhookLogs';
import type { WebhookLog, WebhookLogFilter } from '../../hooks/useWebhookLogs';
import { useFormWebhooks } from '../../hooks/useFormWebhooks';
import { Select } from '../ui/Select';
import { Button } from '../Button';

// =====================================================
// TYPES
// =====================================================

interface WebhookLogViewerProps {
  formId: string;
}

const STATUS_BADGE: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  success: { label: 'Sucesso', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  failed: { label: 'Falhou', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  pending: { label: 'Pendente', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
};

const EVENT_TYPE_LABEL: Record<string, string> = {
  'response.complete': 'Completa',
  'response.partial': 'Parcial',
};

// =====================================================
// COMPONENT
// =====================================================

export const WebhookLogViewer: React.FC<WebhookLogViewerProps> = ({ formId }) => {
  const [filter, setFilter] = useState<WebhookLogFilter>({});
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const { webhooks } = useFormWebhooks(formId);
  const { logs, loading, totalCount, page, pageSize, setPage } = useWebhookLogs(formId, filter);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select
          value={filter.status || 'all'}
          onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value as any }))}
          options={[
            { value: 'all', label: 'Todos os status' },
            { value: 'success', label: 'Sucesso' },
            { value: 'failed', label: 'Falhou' },
            { value: 'pending', label: 'Pendente' },
          ]}
          className="w-40"
        />
        <Select
          value={filter.eventType || 'all'}
          onChange={(e) => setFilter(prev => ({ ...prev, eventType: e.target.value as any }))}
          options={[
            { value: 'all', label: 'Todos os eventos' },
            { value: 'response.complete', label: 'Completas' },
            { value: 'response.partial', label: 'Parciais' },
          ]}
          className="w-40"
        />
        {webhooks.length > 1 && (
          <Select
            value={filter.webhookId || ''}
            onChange={(e) => setFilter(prev => ({ ...prev, webhookId: e.target.value || undefined }))}
            options={[
              { value: '', label: 'Todos os webhooks' },
              ...webhooks.map(w => ({ value: w.id, label: w.name })),
            ]}
            className="w-48"
          />
        )}
        <span className="text-xs text-neutral-500 ml-auto">
          {totalCount} log{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Logs list */}
      {logs.length === 0 ? (
        <div className="text-center py-12 liquid-glass rounded-xl">
          <Webhook className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-400 text-sm">Nenhum log de webhook.</p>
          <p className="text-neutral-500 text-xs mt-1">
            Logs aparecerão aqui quando respostas forem submetidas.
          </p>
        </div>
      ) : (
        <div className="liquid-glass rounded-xl divide-y divide-neutral-800">
          {logs.map(log => {
            const badge = STATUS_BADGE[log.status] || STATUS_BADGE.pending;
            const BadgeIcon = badge.icon;
            const isExpanded = expandedLogId === log.id;

            return (
              <div key={log.id}>
                <button
                  onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-800/30 transition-colors text-left"
                >
                  <span
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${badge.color}`}
                  >
                    <BadgeIcon className="w-3 h-3" />
                    {badge.label}
                  </span>

                  <span className="text-xs text-neutral-300 flex-shrink-0">
                    {log.webhook_name}
                  </span>

                  {log.event_type && (
                    <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                      log.event_type === 'response.partial'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {EVENT_TYPE_LABEL[log.event_type] || log.event_type}
                    </span>
                  )}

                  {log.response_status_code && (
                    <span className="text-xs text-neutral-500 flex-shrink-0">
                      HTTP {log.response_status_code}
                    </span>
                  )}

                  <span className="text-xs text-neutral-500 flex-shrink-0">
                    Tentativa {log.attempt_number}
                  </span>

                  <span className="text-xs text-neutral-600 ml-auto flex-shrink-0">
                    {formatDate(log.created_at)}
                  </span>

                  {isExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 animate-fade-in">
                    {log.error_message && (
                      <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-xs text-red-400">{log.error_message}</p>
                      </div>
                    )}

                    {log.request_payload && (
                      <div>
                        <p className="text-xs text-neutral-500 mb-1">Payload enviado</p>
                        <pre className="text-xs text-neutral-400 bg-neutral-900/50 rounded-lg p-3 overflow-x-auto max-h-40">
                          {JSON.stringify(log.request_payload, null, 2).substring(0, 2000)}
                        </pre>
                      </div>
                    )}

                    {log.response_body && (
                      <div>
                        <p className="text-xs text-neutral-500 mb-1">Resposta recebida</p>
                        <pre className="text-xs text-neutral-400 bg-neutral-900/50 rounded-lg p-3 overflow-x-auto max-h-40">
                          {log.response_body.substring(0, 1000)}
                          {log.response_body.length > 1000 && '...'}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Anterior
          </Button>
          <span className="text-xs text-neutral-500">
            Página {page + 1} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
};
